// `/login` — email + password sign-in for intermediary users.
//
// Why this page is OUTSIDE AuthGate
// ---------------------------------
// Anonymous users must reach `/login` from a fresh tab; AuthGate
// would otherwise redirect them right back here in a loop. The
// top-level dispatcher in `App.tsx` matches `/login` directly,
// before any auth check.
//
// Already-authenticated detection
// -------------------------------
// `useAuth` is unavailable here (AuthContext lives only inside the
// authenticated subtree of AuthGate). Instead the page subscribes
// locally to `onAuthStateChanged` for the single purpose of "if a
// session is already live, send the user home". When the user
// successfully signs in the same listener fires and triggers the
// same redirect, so the page does not have to chain on the mutation
// promise — the source of truth is the Firebase auth observer.
//
// Loop avoidance
// --------------
// On `<Navigate to="/" replace />` the dispatcher delegates to
// `<AuthGate>...<AppRoutes />...</AuthGate>`. AuthGate sees an
// authenticated session, renders the operational subtree, and
// `ApprovalGate` decides between the operational area (ACTIVE) and
// `/approvazione` (everything else). `/login` is never re-entered.

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { GlassCard } from '@ab-platform/ui/glass-card';
import { Banner } from '@ab-platform/ui/banner';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { LoadingState } from '@/components/shared/LoadingState';
import { getFirebaseAuth, isEmulatorMode } from '@/auth/firebase';
import { firebaseClientConfigured } from '@/lib/env';
import { useLogin } from '../hooks/useLogin';
import { mapFirebaseAuthError } from '../lib/firebase-errors';

type ResolvedAuth = 'pending' | 'anonymous' | 'authenticated';

export function LoginPage(): JSX.Element {
  const [resolved, setResolved] = useState<ResolvedAuth>('pending');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useLogin();

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) {
      setResolved('anonymous');
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      setResolved(user ? 'authenticated' : 'anonymous');
    });
    return () => unsub();
  }, []);

  if (!firebaseClientConfigured) {
    return (
      <FocusedPageShell title="Accesso non disponibile">
        <Banner tone="danger">
          La configurazione Firebase non è completa. Riprova più tardi
          o contatta Aurino Broker se il problema persiste.
        </Banner>
      </FocusedPageShell>
    );
  }

  if (resolved === 'pending') {
    return (
      <FocusedPageShell title="Accedi all’area intermediari">
        <LoadingState message="Verifica sessione…" />
      </FocusedPageShell>
    );
  }

  if (resolved === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  return (
    <FocusedPageShell
      title="Accedi all’area intermediari"
      description="Inserisci email e password per accedere."
    >
      <GlassCard className="p-6">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            login.mutate({ email, password });
          }}
        >
          <FloatingLabelInput
            type="email"
            label="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <FloatingLabelInput
            type="password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {login.isError ? (
            <Banner tone="danger">{mapFirebaseAuthError(login.error)}</Banner>
          ) : null}
          <div className="flex justify-center pt-2">
            <PillButton
              type="submit"
              variant="primary"
              size="md"
              disabled={login.isPending}
            >
              {login.isPending ? 'Accesso in corso…' : 'Accedi'}
            </PillButton>
          </div>
        </form>
      </GlassCard>

      <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">
        Hai ricevuto un invito? Usa il link nell’email per impostare la password.
      </p>

      {isEmulatorMode ? (
        <p className="mt-4 text-center text-xs text-state-warning">
          AUTH MODE: emulator
        </p>
      ) : null}
    </FocusedPageShell>
  );
}

export default LoginPage;
